/**
 * Build Your Own Box (BYOB) Bundle JavaScript
 * Supports both standalone page bundles and product-based bundles
 */

class BYOBBundle {
  constructor(section) {
    this.section = section;
    this.products = section.querySelectorAll('[data-bundle-product]');
    this.summaryItems = section.querySelector('[data-summary-items]');
    this.addToCartBtn = section.querySelector('[data-add-bundle-to-cart]');
    this.itemsSelectedEl = section.querySelector('[data-items-selected]');
    this.progressFill = section.querySelector('[data-progress-fill]');

    // Safety check - if no add to cart button, don't initialize
    if (!this.addToCartBtn) {
      console.warn('Bundle: No add to cart button found');
      return;
    }

    this.itemsRequired = parseInt(this.addToCartBtn.dataset.itemsRequired) || 4;
    this.bundleVariantId = this.addToCartBtn.dataset.bundleVariantId || null;
    this.bundleType = this.addToCartBtn.dataset.bundleType || 'bundle';

    this.selectedItems = [];

    this.init();
  }

  init() {
    // Product toggle buttons
    this.products.forEach(product => {
      const toggleBtn = product.querySelector('[data-toggle-product]');

      if (toggleBtn) {
        toggleBtn.addEventListener('click', (e) => {
          e.stopPropagation();
          this.toggleProduct(product);
        });
      }

      // Also allow clicking the whole card (except variant select)
      product.addEventListener('click', (e) => {
        if (e.target.closest('[data-toggle-product]') || e.target.closest('[data-variant-select]')) return;
        this.toggleProduct(product);
      });
    });

    // Add to cart button
    if (this.addToCartBtn) {
      this.addToCartBtn.addEventListener('click', (e) => {
        e.preventDefault();
        this.addBundleToCart();
      });
    }

    // Bundle initialized
  }

  toggleProduct(product) {
    const productId = product.dataset.productId;
    const variantId = product.dataset.variantId || null;
    const variantSelect = product.querySelector('[data-variant-select]');

    if (product.classList.contains('is-selected')) {
      // Remove from selection
      product.classList.remove('is-selected');
      this.selectedItems = this.selectedItems.filter(item => item.productId !== productId);
    } else {
      // Check if we've reached the limit
      if (this.selectedItems.length >= this.itemsRequired) {
        this.showMessage(`You can only select ${this.itemsRequired} items`);
        return;
      }

      // Add to selection
      product.classList.add('is-selected');

      // Get the display title from the product card
      const titleEl = product.querySelector('.byob-bundle__product-title') ||
                      product.querySelector('.product-bundle__product-title');
      const displayTitle = titleEl ? titleEl.textContent.trim() : 'Item';

      const image = product.querySelector('.byob-bundle__product-image img') ||
                    product.querySelector('.product-bundle__product-image img');
      const imageSrc = image ? image.src : '';

      // Use variantId from data attribute if available, otherwise from select
      const finalVariantId = variantId || (variantSelect ? variantSelect.value : null);

      this.selectedItems.push({
        productId,
        variantId: finalVariantId,
        displayTitle, // The title shown in the UI (variant name for samples, product name for gift box)
        imageSrc,
        element: product
      });
    }

    this.updateUI();
  }

  updateUI() {
    // Update count displays
    const countEls = this.section.querySelectorAll('[data-items-selected]');
    countEls.forEach(el => {
      el.textContent = this.selectedItems.length;
    });

    // Update progress bar
    if (this.progressFill) {
      const percentage = (this.selectedItems.length / this.itemsRequired) * 100;
      this.progressFill.style.width = percentage + '%';
    }

    // Update bottom bar with thumbnail circles
    const bottomBar = this.section.querySelector('.product-bundle__bottom-bar');
    if (bottomBar) {
      if (this.selectedItems.length > 0) {
        bottomBar.classList.add('has-selections');
      } else {
        bottomBar.classList.remove('has-selections');
      }
    }

    // Build thumbnail display for bottom bar
    if (this.summaryItems) {
      const countBadge = `<span class="product-bundle__bottom-bar-count"><span data-items-selected>${this.selectedItems.length}</span>/${this.itemsRequired}</span>`;

      if (this.selectedItems.length === 0) {
        this.summaryItems.innerHTML = countBadge;
      } else {
        const thumbs = this.selectedItems.map(item => `
          <div class="product-bundle__bottom-bar-thumb" data-summary-product-id="${item.productId}" title="${item.displayTitle}">
            <img src="${item.imageSrc}" alt="${item.displayTitle}">
            <button type="button" class="product-bundle__bottom-bar-thumb-remove" data-remove-item="${item.productId}" aria-label="Remove">×</button>
          </div>
        `).join('');

        this.summaryItems.innerHTML = countBadge + thumbs;

        // Add remove listeners
        this.summaryItems.querySelectorAll('[data-remove-item]').forEach(btn => {
          btn.addEventListener('click', (e) => {
            e.stopPropagation();
            const productId = btn.dataset.removeItem;
            const product = this.section.querySelector(`[data-product-id="${productId}"]`);
            if (product) {
              this.toggleProduct(product);
            }
          });
        });
      }
    }

    // Update button
    if (this.selectedItems.length === this.itemsRequired) {
      this.addToCartBtn.disabled = false;
      this.addToCartBtn.textContent = 'Add Bundle to Cart';
    } else {
      this.addToCartBtn.disabled = true;
      const remaining = this.itemsRequired - this.selectedItems.length;
      this.addToCartBtn.textContent = `Select ${remaining} More`;
    }
  }

  async addBundleToCart() {
    if (this.selectedItems.length !== this.itemsRequired) return;

    this.addToCartBtn.disabled = true;
    this.addToCartBtn.textContent = 'Adding...';

    try {
      // Build the line item properties with selected items
      const properties = {};
      this.selectedItems.forEach((item, index) => {
        const itemLabel = `Item ${index + 1}`;
        properties[itemLabel] = item.displayTitle;
      });
      properties['_bundle_type'] = this.bundleType;

      // If we have a bundle variant ID, add the bundle product to cart
      if (this.bundleVariantId) {
        const response = await fetch('/cart/add.js', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            id: parseInt(this.bundleVariantId),
            quantity: 1,
            properties: properties
          })
        });

        if (!response.ok) {
          throw new Error('Failed to add to cart');
        }
      } else {
        // Fallback: add individual items (for standalone page bundles)
        const bundleId = Date.now().toString();
        const items = this.selectedItems.map((item, index) => ({
          id: parseInt(item.variantId),
          quantity: 1,
          properties: {
            '_bundle_type': this.bundleType,
            '_bundle_id': bundleId,
            '_bundle_item': `${index + 1} of ${this.itemsRequired}`
          }
        }));

        const response = await fetch('/cart/add.js', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({ items })
        });

        if (!response.ok) {
          throw new Error('Failed to add to cart');
        }
      }

      // Success
      this.addToCartBtn.textContent = 'Added!';

      // Refresh cart drawer content first, then open
      setTimeout(async () => {
        // Refresh cart drawer content BEFORE opening
        if (window.cart && typeof window.cart.refresh === 'function') {
          await window.cart.refresh();
        }

        // Now open the cart drawer with updated content
        const cartDrawer = document.querySelector('.js-cart-drawer');
        if (cartDrawer) {
          cartDrawer.classList.add('is-open');
          document.body.classList.add('no-scroll');
        }

        // Refresh cart count
        this.updateCartCount();

        // Reset selection
        this.resetSelection();
      }, 300);

    } catch (error) {
      console.error('Bundle add to cart error:', error);
      this.addToCartBtn.textContent = 'Error - Try Again';
      setTimeout(() => {
        this.updateUI();
      }, 2000);
    }
  }

  async updateCartCount() {
    try {
      const response = await fetch('/cart.js');
      const cart = await response.json();

      const countEls = document.querySelectorAll('.js-cart-count');
      countEls.forEach(el => {
        el.textContent = cart.item_count;
        if (cart.item_count > 0) {
          el.style.display = '';
        }
      });
    } catch (error) {
      console.error('Failed to update cart count:', error);
    }
  }

  resetSelection() {
    this.selectedItems = [];
    this.products.forEach(product => {
      product.classList.remove('is-selected');
    });
    this.updateUI();
  }

  showMessage(message) {
    // Simple alert for now - could be enhanced with a toast notification
    alert(message);
  }
}

// Initialize all BYOB sections
document.addEventListener('DOMContentLoaded', () => {
  document.querySelectorAll('[data-bundle-section]').forEach(section => {
    new BYOBBundle(section);
  });
});
